import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '../atoms/DatePicker';
import { Checkbox } from '../ui/checkbox';
import { FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

export type AoeQuestionnaireFieldsProps = {
  /** The (normalized) AOE questionnaire to render one input per item for. */
  questionnaire: Questionnaire;
  /** The in-progress response, or `undefined` before any answer is given. */
  response: QuestionnaireResponse | undefined;
  /** Called with the full updated response whenever any answer changes. */
  onChange: (response: QuestionnaireResponse) => void;
  /** linkIds of required items that are still unanswered — rendered with an error treatment. */
  invalidLinkIds?: ReadonlySet<string>;
};

const firstAnswer = (
  response: QuestionnaireResponse | undefined,
  linkId: string,
): QuestionnaireResponseItemAnswer | undefined => response?.item?.find((i) => i.linkId === linkId)?.answer?.[0];

/**
 * Upsert a single item's answer into the response, preserving the other items. Answers are stored
 * flat (one top-level `item` per linkId) even for items nested under a `group`; `firstAnswer` reads
 * them back the same way, so the round-trip is self-consistent. This deliberately flattens the FHIR
 * nesting — keep reader and writer in sync if that ever needs to change.
 */
const upsertAnswer = (
  response: QuestionnaireResponse | undefined,
  questionnaire: Questionnaire,
  item: QuestionnaireItem,
  answer: QuestionnaireResponseItemAnswer | undefined,
): QuestionnaireResponse => {
  const base: QuestionnaireResponse = response ?? {
    resourceType: 'QuestionnaireResponse',
    status: 'in-progress',
    ...(questionnaire.url ? { questionnaire: questionnaire.url } : {}),
  };
  const items = (base.item ?? []).filter((i) => i.linkId !== item.linkId);
  if (answer) items.push({ linkId: item.linkId, text: item.text, answer: [answer] });
  return { ...base, item: items };
};

const optionValue = (option: NonNullable<QuestionnaireItem['answerOption']>[number]): string =>
  option.valueCoding?.code ?? option.valueString ?? '';

const optionLabel = (option: NonNullable<QuestionnaireItem['answerOption']>[number]): string =>
  option.valueCoding?.display ?? option.valueCoding?.code ?? option.valueString ?? '';

/** FHIR extension HG uses to declare the unit for a `quantity` questionnaire item. */
const QUESTIONNAIRE_UNIT_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-unit';

type ControlProps = {
  item: QuestionnaireItem;
  current: QuestionnaireResponseItemAnswer | undefined;
  invalid: boolean;
  label: string;
  onAnswer: (answer: QuestionnaireResponseItemAnswer | undefined) => void;
};

const renderNumberControl = ({ item, current, invalid, label, onAnswer }: ControlProps): React.ReactNode => {
  const isQuantity = item.type === 'quantity';
  const value = (isQuantity ? current?.valueQuantity?.value : (current?.valueDecimal ?? current?.valueInteger)) ?? '';
  // HG declares a quantity item's unit via the `questionnaire-unit` extension; carry it onto the
  // answer so the unit isn't dropped (some labs reject unit-less quantities).
  const unit = item.extension?.find((e) => e.url === QUESTIONNAIRE_UNIT_URL)?.valueCoding;
  const unitLabel = unit?.display ?? unit?.code;
  return (
    <Input
      id={`aoe-${item.linkId}`}
      type="number"
      value={value}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') return onAnswer(undefined);
        if (isQuantity)
          return onAnswer({
            valueQuantity: {
              value: Number.parseFloat(raw),
              ...(unitLabel && { unit: unitLabel }),
              ...(unit?.system && { system: unit.system }),
              ...(unit?.code && { code: unit.code }),
            },
          });
        if (item.type === 'integer') return onAnswer({ valueInteger: Number.parseInt(raw, 10) });
        onAnswer({ valueDecimal: Number.parseFloat(raw) });
      }}
      aria-label={label}
      aria-invalid={invalid}
      aria-required={item.required ?? false}
    />
  );
};

const renderChoiceControl = ({ item, current, invalid, label, onAnswer }: ControlProps): React.ReactNode => {
  const options = (item.answerOption ?? []).filter((o) => optionValue(o) !== '');
  return (
    <Select
      value={current?.valueCoding?.code ?? current?.valueString ?? ''}
      onValueChange={(value) => {
        const option = options.find((o) => optionValue(o) === value);
        if (!option) return onAnswer(undefined);
        onAnswer(option.valueCoding ? { valueCoding: option.valueCoding } : { valueString: option.valueString });
      }}
    >
      <SelectTrigger
        id={`aoe-${item.linkId}`}
        className="w-full"
        aria-label={label}
        aria-invalid={invalid}
        aria-required={item.required ?? false}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={optionValue(option)} value={optionValue(option)}>
            {optionLabel(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const renderStringControl = ({ item, current, invalid, label, onAnswer }: ControlProps): React.ReactNode => {
  const id = `aoe-${item.linkId}`;
  const onText = (text: string): void => onAnswer(text ? { valueString: text } : undefined);
  if (item.type === 'text') {
    return (
      <Textarea
        id={id}
        value={current?.valueString ?? ''}
        onChange={(e) => onText(e.target.value)}
        aria-label={label}
        aria-invalid={invalid}
        aria-required={item.required ?? false}
        rows={2}
      />
    );
  }
  return (
    <Input
      id={id}
      value={current?.valueString ?? ''}
      onChange={(e) => onText(e.target.value)}
      aria-label={label}
      aria-invalid={invalid}
      aria-required={item.required ?? false}
    />
  );
};

/** Render the input control for a single (non-group, non-display) AOE item. */
function renderControl(props: ControlProps): React.ReactNode {
  const { item, current, invalid, label, onAnswer } = props;
  switch (item.type) {
    case 'boolean':
      return (
        <Checkbox
          id={`aoe-${item.linkId}`}
          checked={current?.valueBoolean ?? false}
          onCheckedChange={(checked) => onAnswer(checked === true ? { valueBoolean: true } : undefined)}
          aria-label={label}
          aria-required={item.required ?? false}
        />
      );
    case 'date':
      return (
        <DatePicker
          id={`aoe-${item.linkId}`}
          value={current?.valueDate ?? ''}
          onChange={(value) => onAnswer(value ? { valueDate: value } : undefined)}
          aria-label={label}
          aria-invalid={invalid}
          aria-required={item.required ?? false}
        />
      );
    case 'integer':
    case 'decimal':
    case 'quantity':
      return renderNumberControl(props);
    case 'choice':
    case 'open-choice':
      return renderChoiceControl(props);
    default:
      return renderStringControl(props);
  }
}

/**
 * Renders editable inputs for an Ask-at-Order-Entry (AOE) `Questionnaire`, one control per item
 * keyed by item type (string/text/boolean/number/quantity/date/choice). Pure and controlled: it
 * reads answers from `response` and emits a fully-rebuilt `QuestionnaireResponse` on every change.
 * Required-item validation is the caller's job — pass unanswered required linkIds as `invalidLinkIds`.
 */
export const AoeQuestionnaireFields = ({
  questionnaire,
  response,
  onChange,
  invalidLinkIds,
}: AoeQuestionnaireFieldsProps): React.ReactElement => {
  const { t } = useTranslation('records');

  const renderItem = (item: QuestionnaireItem): React.ReactNode => {
    if (item.type === 'group') {
      return (
        <fieldset key={item.linkId} className="space-y-3">
          {item.text && <legend className="text-sm font-medium">{item.text}</legend>}
          {item.item?.map(renderItem)}
        </fieldset>
      );
    }
    if (item.type === 'display') {
      return (
        <p key={item.linkId} className="text-sm text-muted-foreground">
          {item.text}
        </p>
      );
    }

    const id = `aoe-${item.linkId}`;
    const label = item.text ?? item.linkId;
    const invalid = invalidLinkIds?.has(item.linkId) ?? false;
    const current = firstAnswer(response, item.linkId);
    const onAnswer = (answer: QuestionnaireResponseItemAnswer | undefined): void =>
      onChange(upsertAnswer(response, questionnaire, item, answer));
    const control = renderControl({ item, current, invalid, label, onAnswer });
    // Label on the left, control filling the right — `w-20` matches the per-test Priority/Notes
    // rows so the AOE controls line up with (and are as wide as) those fields.
    const labelEl = (
      <FieldLabel htmlFor={id} className="w-20 shrink-0">
        {label}
        {item.required && <span aria-hidden="true"> *</span>}
      </FieldLabel>
    );

    if (item.type === 'boolean') {
      return (
        <div key={item.linkId} className="flex items-center gap-2">
          {labelEl}
          {control}
        </div>
      );
    }

    return (
      <div key={item.linkId} className="space-y-1">
        <div className="flex items-center gap-2">
          {labelEl}
          <div className="flex-1 *:w-full">{control}</div>
        </div>
        {invalid && <p className="text-sm text-destructive">{t('requiredAnswer')}</p>}
      </div>
    );
  };

  return <div className="space-y-3">{(questionnaire.item ?? []).map(renderItem)}</div>;
};
